import { defineStore } from 'pinia';
import { contributorsPreview } from '🔗/utils/audio';

const defaulUIConfig = {
    carousel: true,       // 1. Карусель с обложкой
    interactive: false,   // 2. Интерактивен ли сам плагин (пропуск кликов сквозь iframe)
    trackInfo: true,      // 3. Блок с названием, автором + кебаб
    progressBar: true,    // 4. Полоса прогресса
    controls: {           // 5. Кнопки управления
        like: true,
        dislike: true,
        playPause: true,
        next: true,
        prev: true
    },
    actions: {            // 6. Дополнительные кнопки
        repeat: true,
        playlist: true
    },
    minimize: true        // 7. Кнопка свернуть плеер
}

const loadPlugin = () => {
    let plugin = null;

    try {
        const data = localStorage.getItem('player-plugin');

        if (data) {
            plugin = JSON.parse(data);
        }
    } catch (e) {
        console.error('Ошибка парсинга localStorage:', e);
    }

    return plugin;
}

const usePlayerPluginStore = defineStore('player-plugin', {
    state: () => ({
        isIframeReady: false,
        frame: null,
        plugin: loadPlugin(),
        uiConfig: { ...defaulUIConfig }
    }),
    actions: {
        setPlugin(item) {
            if (!item?.code) {
                return;
            }

            this.isIframeReady = false;

            this.plugin = {
                code: item.code,
                // url: `http://localhost:5173/#/plugins/${item.code}` // Temp, вытащить в env
                url: `https://plugins.bilbomusic.com/#/plugins/${item.code}` // Temp, вытащить в env
            }

            localStorage.setItem('player-plugin', JSON.stringify(this.plugin))
        },
        clearPlugin() {
            delete this.plugin;
            this.updateUIConfig({ ...defaulUIConfig });

            localStorage.removeItem('player-plugin');
        },
        setFrame(frame) {
            this.frame = frame;
        },
        updateUIConfig(payload) {
            this.uiConfig = {
                ...defaulUIConfig,
                ...payload,
                controls: {
                    ...defaulUIConfig.controls,
                    ...(payload.controls || {})
                },
                actions: {
                    ...defaulUIConfig.actions,
                    ...(payload.actions || {})
                }
            }
        },
        getImage(item) {
            return {
                crop: import.meta.env.VITE_APP_FILES_URL + item?.crop,
                original: import.meta.env.VITE_APP_FILES_URL + item?.original,
                resized: import.meta.env.VITE_APP_FILES_URL + item?.resized
            }
        },
        getArtists(items, role) {
            return items?.filter(item => item.role === role).map(item => ({
                name: item.artist?.name || item.unregisteredArtistName,
                profileImage: item.artist?.profileImage ? this.getImage(item.artist.profileImage) : null
            }))
        },

        beautyTrack(track) {
            if (!track) {
                return;
            }

            return {
                id: track.id,
                bpm: track.bpm,
                cover: this.getImage(track.cover),
                duration: track.duration,
                fromProject: track.fromProject,
                isExplicit: track.isExplicit,
                title: track.title,
                version: track.version,
                wave: track.wave,
                contributors: {
                    primary: this.getArtists(track.contributors, 'PRIMARY'),
                    featured: this.getArtists(track.contributors, 'FEATURED'),
                },
                artists: contributorsPreview(track.contributors)
            }
        },

        beautyRelease(release) {
            if (!release) {
                return;
            }

            return {
                id: release.id,
                title: release.title,
                cover: this.getImage(release.cover),
                fromProject: release.fromProject,
                contributors: {
                    primary: this.getArtists(release.contributors, 'PRIMARY'),
                    featured: this.getArtists(release.contributors, 'FEATURED'),
                },
                artists: contributorsPreview(release.contributors)
            }
        },

        beautyArtist(artist) {
            if (!artist) {
                return;
            }

            return {
                id: artist.id,
                name: artist.name,
                code: artist.code,
                profileImage: this.getImage(artist.profileImage)
            }
        },

        beautyPlaylist(playlist) {
            if (!playlist) {
                return;
            }

            return {
                id: playlist.id,
                name: playlist.name,
                code: playlist.code,
                cover: this.getImage(playlist.cover),
                title: stores.locale().title(playlist.title),
                type: playlist.type
            }
        },

        beautyQueue(queue) {
            if (!queue) {
                return;
            }

            return {
                items: queue.items?.map(item => this.beautyTrack(item)),
                release: this.beautyRelease(queue.release),
                artist: this.beautyArtist(queue.artist),
                playlist: this.beautyPlaylist(queue.playlist)
            }
        },

        buildMessage(pane, type, payload) {
            const message = {
                pane,
                type,
                payload: {...payload || {}}
            }


            if (message.payload?.item) {
                if (this.track?.id !== message.payload?.item?.id) {

                    this.track = this.beautyTrack(message.payload.item);
                    message.payload.track = this.track;
                }
            }
            delete message.payload?.item;

            if (!message.pane) {
                delete message.pane;
            }


            if (message.type === 'queue' && message.payload) {
                message.payload = this.beautyQueue(message.payload);
            }

            if (!message.payload || !Object.keys(message.payload).length) {
                delete message.payload;
            }

            return message;
        },

        getStyleTokens() {
            const rootStyles = window.getComputedStyle(document.documentElement);
            const styles = {};

            const tokens = [
                '--max-safe-area-inset-top',
                '--tg-safe-area-inset-top',
                '--max-content-safe-area-inset-top',
                '--tg-content-safe-area-inset-top'
            ]

            tokens.forEach(style => {
                styles[style] = rootStyles.getPropertyValue(style).trim();
            });

            return styles;
        },

        sendInitMessage() {
            if (!this.frame?.contentWindow || !this.isIframeReady) {
                return
            }

            const message = {
                type: 'PLAYER_INIT',
                payload: {
                    locale: {
                        locale: stores.locale().locale,
                        rtl: stores.locale().rtl
                    },
                    theme: stores.settings().theme,
                    vibration: stores.settings().vibration,
                    player: {
                        expanded: stores.player().expanded
                    },
                    panes: {
                        default: {
                            track: this.beautyTrack(stores.player().panes?.default?._item),
                            queue: this.beautyQueue(stores.player().panes?.default?._queue),
                            position: stores.player().panes?.default?._playedSeconds * 1000,
                            state: stores.player().panes?.default?._state,
                            prevDisabled: !!stores.player().panes?.default?.prevDisabled,
                            nextDisabled: !!stores.player().panes?.default?.nextDisabled,
                            hasQueue: !!stores.player().panes?.default?.hasQueue,
                            repeat: stores.player().panes?.default?._repeat || 'none',
                            reaction: null
                        }
                    },
                    styles: this.getStyleTokens(),
                    user: {
                        id: stores.user().profile?.id
                    }
                }
            }

            this.frame.contentWindow.postMessage(JSON.parse(JSON.stringify(message)), '*');
        },

        sendTrackReaction(value) {
            if (!this.frame?.contentWindow || !this.isIframeReady) {
                return
            }

            const message = {
                type: 'reaction',
                pane: 'default',
                payload: {
                    reaction: value
                }
            }

            this.frame.contentWindow.postMessage(JSON.parse(JSON.stringify(message)), '*');
        },

        send({ pane, type, payload }) {
            if (!this.frame?.contentWindow || !this.isIframeReady) {
                return
            }

            const message = this.buildMessage(pane, type, payload);

            if (message) {
                this.frame.contentWindow.postMessage(JSON.parse(JSON.stringify(message)), '*');
            }
        },
        sendBuffer({ pane, type, buffer }) {
            if (!this.frame?.contentWindow || !this.isIframeReady) {
                return
            }

            this.frame.contentWindow.postMessage({
                pane,
                type,
                payload: { buffer }
            }, '*', [buffer]);
        }
    }
});

export default usePlayerPluginStore;
